import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { ApolloService } from '../../services/apollo.service';
import { ReactiveContainer } from '../../containers/reactive-container';
import { JobDetail } from '../../entities/jobDetail/jobDetail';
import { nextJobExecution, worstTriggerState } from '../job-admin/job-admin-labels';
import * as fromGraphQl from '../job-admin/graphql';

export interface JobRow {
  job: JobDetail;
  status: string;
  nextExecution: string | null;
}

@Component({
  selector: 'app-job-list',
  templateUrl: './job-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JobListComponent implements OnInit {
  private readonly apolloService = inject(ApolloService);
  private readonly router = inject(Router);

  readonly jobGroups = signal<string[]>([]);
  readonly selectedGroup = signal<string | null>(null);

  private readonly container = new ReactiveContainer<fromGraphQl.JobDetailsResponse>(
    fromGraphQl.JOB_DETAILS_QUERY,
    { pollInterval: 15000, stopPollingOnSuccess: false, useGlobalLoading: false }
  );

  readonly loading = this.container.loading;
  readonly error = this.container.error;

  readonly data = this.container.fromSignals(
    [this.selectedGroup],
    ([group]) => ({ group }),
    { skipWhen: ([group]) => !group }
  );

  readonly jobs = this.container.selectWithDefault(
    d => (d.getJobDetails ?? []).map(payload => new JobDetail(payload)),
    []
  );

  readonly rows = computed<JobRow[]>(() =>
    this.jobs().map(job => ({
      job,
      status: worstTriggerState(job.jobTriggers),
      nextExecution: nextJobExecution(job.jobTriggers)
    }))
  );

  ngOnInit(): void {
    this.apolloService
      .query<fromGraphQl.JobGroupsResponse>(fromGraphQl.JOB_GROUPS_QUERY)
      .pipe(take(1))
      .subscribe(result => this.jobGroups.set(result.data?.getJobGroups ?? []));
  }

  onGroupChange(group: string | null): void {
    this.selectedGroup.set(group ?? null);
  }

  reload(): void {
    const group = this.selectedGroup();
    if (group) {
      this.container.refetch({ group });
    }
  }

  openJob(job: JobDetail): void {
    this.router.navigate(['settings/job-admin/job', job.key.group, job.key.name]);
  }
}
